/** Centralized TanStack Query keys — one place so invalidation is predictable
 *  and no two hooks accidentally diverge on key shape. */
import type { RequestFilters, TimeWindow } from '@/shared/types';

interface Scope {
  window: TimeWindow;
  env: string;
}

export const queryKeys = {
  // ---- existing analytics ----
  summary: (s: Scope) => ['summary', s] as const,
  timeseries: (s: Scope) => ['timeseries', s] as const,
  statusCodes: (s: Scope) => ['statusCodes', s] as const,
  errorCodes: (s: Scope) => ['errorCodes', s] as const,
  latencyHistogram: (s: Scope) => ['latencyHistogram', s] as const,
  models: (s: Scope) => ['models', s] as const,
  modelDetail: (id: string, s: Scope) => ['modelDetail', id, s] as const,
  requests: (s: Scope, filters: RequestFilters) => ['requests', s, filters] as const,
  requestDetail: (id: string) => ['requestDetail', id] as const,
  health: () => ['health'] as const,
  ready: () => ['ready'] as const,
  system: (s: Scope) => ['system', s] as const,
  loginStats: (s: Pick<Scope, 'window'>) => ['loginStats', s] as const,

  // ---- admin: client management ----
  clients: () => ['admin', 'clients'] as const,
  client: (id: string) => ['admin', 'client', id] as const,
  clientTransactions: (id: string, cursor?: string, filters?: unknown) =>
    ['admin', 'clientTransactions', id, cursor, filters] as const,
  clientRequests: (id: string, cursor?: string, filters?: unknown) =>
    ['admin', 'clientRequests', id, cursor, filters] as const,
  clientUsage: (id: string) => ['admin', 'clientUsage', id] as const,
  clientFilters: (id: string) => ['admin', 'clientFilters', id] as const,
  clientCatalog: (id: string) => ['admin', 'clientCatalog', id] as const,
  clientBilling: (id: string, year: number, month: number, from: string, to: string, model?: string) =>
    ['admin', 'clientBilling', id, year, month, from, to, model] as const,
  clientBills: (id: string) => ['admin', 'clientBills', id] as const,
  modelCatalog: () => ['admin', 'modelCatalog'] as const,
  modelRequests: (status?: string) => ['admin', 'modelRequests', status] as const,

  // ---- admin: pricing ----
  pricing: () => ['admin', 'pricing'] as const,

  // ---- admin: provider credentials ----
  providers: () => ['admin', 'providers'] as const,

  // ---- client portal ----
  myProfile: () => ['client', 'profile'] as const,
  myBalance: () => ['client', 'balance'] as const,
  myRequests: (cursor?: string) => ['client', 'requests', cursor] as const,
  myTransactions: (cursor?: string) => ['client', 'transactions', cursor] as const,
  myCatalog: () => ['client', 'catalog'] as const,
  myUsage: () => ['client', 'usage'] as const,
  myModelRequests: () => ['client', 'modelRequests'] as const,
  myBills: () => ['client', 'bills'] as const,
};
